import { zodResolver } from "@hookform/resolvers/zod";
import { isAxiosError } from "axios";
import { FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";

import { SubCategoryForm } from "../sub-categories/sub-category-form";

import { FormError, FormModal } from "@/components/kit";
import { useEditSubCategory } from "@/hooks/api/category/useEditSubCategory";
import {
  SUB_CATEGORY_FORM_SCHEMA,
  SubCategoryFormData,
} from "@/schemas/subcategory-form.schema.";
import { SubCategory } from "@/types";

interface EditSubCategoryModalProps {
  categoryId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  subCategory?: SubCategory;
}

export function EditSubCategoryModal({
  categoryId,
  isOpen,
  onClose,
  onSuccess,
  subCategory,
}: EditSubCategoryModalProps) {
  const {
    mutate: createSubCategory,
    isPending: isCreatingSubCategory,
    isError,
    error,
  } = useEditSubCategory(categoryId, subCategory?.id!);
  const form = useForm<SubCategoryFormData>({
    resolver: zodResolver(SUB_CATEGORY_FORM_SCHEMA),
  });

  const handleSubmit = (values: SubCategoryFormData) => {
    createSubCategory(values, {
      onSuccess: () => {
        onSuccess();
      },
    });
  };

  const errorMessage = isError
    ? isAxiosError(error)
      ? error.response?.data?.message
      : "Failed to create sub category, please try again"
    : null;

  useEffect(() => {
    if (subCategory) {
      form.reset({
        id: subCategory.id,
        image: subCategory.image,
        name: subCategory.name,
        nameAr: subCategory.nameAr,
      });
    }
  }, [subCategory]);

  return (
    <FormModal
      isOpen={isOpen}
      isSubmitting={isCreatingSubCategory}
      submitLabel="Save"
      title="Edit Subcategory"
      onClose={onClose}
      onSubmit={() => form.handleSubmit(handleSubmit)()}
    >
      <FormProvider {...form}>
        <FormError message={errorMessage} />
        <SubCategoryForm disabledFields={["id"]} />
      </FormProvider>
    </FormModal>
  );
}
